import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { contactInfo } from '../utils/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    institution: '',
    message: ''
  });

  const [workshopForm, setWorkshopForm] = useState({
    institutionName: '',
    contactPerson: '',
    email: '',
    phone: '',
    studentCount: '',
    workshopType: '',
    preferredDate: '',
    additionalInfo: ''
  });

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    // Save to database
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, contactForm);
    } catch (err) {
      console.error('Failed to save contact inquiry:', err);
    }
    
    // Prepare WhatsApp message
    const message = `*New Contact Form Submission*\n\n` +
      `Name: ${contactForm.name}\n` +
      `Email: ${contactForm.email}\n` +
      `Phone: ${contactForm.phone || 'Not provided'}\n` +
      `Institution: ${contactForm.institution || 'Not provided'}\n\n` +
      `Message:\n${contactForm.message}`;
    
    // Open WhatsApp with pre-filled message
    const whatsappURL = `https://wa.me/919762717034?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
    
    toast.success('Redirecting to WhatsApp. Please send the message to complete your inquiry!');
    
    // Reset form
    setContactForm({
      name: '',
      email: '',
      phone: '',
      institution: '',
      message: ''
    });
  };

  const handleWorkshopSubmit = async (e) => {
    e.preventDefault();

    // Save to database
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, workshopForm);
    } catch (err) {
      console.error('Failed to save workshop booking:', err);
    }
    
    // Prepare WhatsApp message
    const message = `*New Workshop Booking Request*\n\n` +
      `Institution: ${workshopForm.institutionName}\n` +
      `Contact Person: ${workshopForm.contactPerson}\n` +
      `Email: ${workshopForm.email}\n` +
      `Phone: ${workshopForm.phone}\n` +
      `Student Count: ${workshopForm.studentCount}\n` +
      `Workshop Type: ${workshopForm.workshopType}\n` +
      `Preferred Date: ${workshopForm.preferredDate || 'Not specified'}\n\n` +
      `Additional Info:\n${workshopForm.additionalInfo || 'None'}`;
    
    // Open WhatsApp with pre-filled message
    const whatsappURL = `https://wa.me/919762717034?text=${encodeURIComponent(message)}`;
    window.open(whatsappURL, '_blank');
    
    toast.success('Redirecting to WhatsApp. Please send the message to complete your booking!');
    
    // Reset form
    setWorkshopForm({
      institutionName: '',
      contactPerson: '',
      email: '',
      phone: '',
      studentCount: '',
      workshopType: '',
      preferredDate: '',
      additionalInfo: ''
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get in <span className="text-cyan-400">Touch</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Have questions or want to book a workshop? We'd love to hear from you!
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                <Mail className="text-cyan-400" size={24} />
              </div>
              <h3 className="text-white font-semibold mb-2">Email Us</h3>
              <p className="text-gray-400 text-sm">{contactInfo.email}</p>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                <Phone className="text-cyan-400" size={24} />
              </div>
              <h3 className="text-white font-semibold mb-2">Call Us</h3>
              <div className="space-y-1">
                <p className="text-gray-400 text-sm">{contactInfo.phone}</p>
                <p className="text-gray-400 text-sm">{contactInfo.phone2}</p>
              </div>
            </div>

            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl p-6 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/20">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="text-cyan-400" size={24} />
              </div>
              <h3 className="text-white font-semibold mb-2">Visit Us</h3>
              <p className="text-gray-400 text-sm">{contactInfo.address}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">Send a Message</h2>
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="contact-name" className="text-gray-300">Name *</Label>
                  <Input
                    id="contact-name"
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-email" className="text-gray-300">Email *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-phone" className="text-gray-300">Phone</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="+91 98765 43210"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-institution" className="text-gray-300">Institution</Label>
                  <Input
                    id="contact-institution"
                    type="text"
                    value={contactForm.institution}
                    onChange={(e) => setContactForm({ ...contactForm, institution: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="Your school/college name"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-gray-300">Message *</Label>
                  <Textarea
                    id="contact-message"
                    required
                    value={contactForm.message}
                    onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500 min-h-[120px]"
                    placeholder="How can we help you?"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-6"
                >
                  <Send size={18} className="mr-2" />
                  Send Message
                </Button>
              </form>
            </div>

            {/* Workshop Booking Form */}
            <div className="bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-white mb-6">Book a Workshop</h2>
              <form onSubmit={handleWorkshopSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="workshop-institution" className="text-gray-300">Institution Name *</Label>
                  <Input
                    id="workshop-institution"
                    type="text"
                    required
                    value={workshopForm.institutionName}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, institutionName: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="School/College name"
                  />
                </div>

                <div>
                  <Label htmlFor="workshop-contact-person" className="text-gray-300">Contact Person *</Label>
                  <Input
                    id="workshop-contact-person"
                    type="text"
                    required
                    value={workshopForm.contactPerson}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, contactPerson: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    placeholder="Coordinator name"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="workshop-email" className="text-gray-300">Email *</Label>
                    <Input
                      id="workshop-email"
                      type="email"
                      required
                      value={workshopForm.email}
                      onChange={(e) => setWorkshopForm({ ...workshopForm, email: e.target.value })}
                      className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                      placeholder="email@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="workshop-phone" className="text-gray-300">Phone *</Label>
                    <Input
                      id="workshop-phone"
                      type="tel"
                      required
                      value={workshopForm.phone}
                      onChange={(e) => setWorkshopForm({ ...workshopForm, phone: e.target.value })}
                      className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="workshop-student-count" className="text-gray-300">Student Count *</Label>
                    <Input
                      id="workshop-student-count"
                      type="number"
                      required
                      value={workshopForm.studentCount}
                      onChange={(e) => setWorkshopForm({ ...workshopForm, studentCount: e.target.value })}
                      className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                      placeholder="50"
                    />
                  </div>

                  <div>
                    <Label htmlFor="workshop-date" className="text-gray-300">Preferred Date</Label>
                    <Input
                      id="workshop-date"
                      type="date"
                      value={workshopForm.preferredDate}
                      onChange={(e) => setWorkshopForm({ ...workshopForm, preferredDate: e.target.value })}
                      className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="workshop-type" className="text-gray-300">Workshop Type *</Label>
                  <select
                    id="workshop-type"
                    required
                    value={workshopForm.workshopType}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, workshopType: e.target.value })}
                    className="mt-2 w-full px-3 py-2 bg-slate-800/50 border border-cyan-500/30 rounded-md text-white focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  >
                    <option value="">Select workshop type</option>
                    <option value="ai-robotics">AI & Robotics</option>
                    <option value="iot">IoT Training</option>
                    <option value="3d-printing">3D Printing</option>
                    <option value="pcb-design">PCB Design</option>
                    <option value="custom">Custom Workshop</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="workshop-additional-info" className="text-gray-300">Additional Information</Label>
                  <Textarea
                    id="workshop-additional-info"
                    value={workshopForm.additionalInfo}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, additionalInfo: e.target.value })}
                    className="mt-2 bg-slate-800/50 border-cyan-500/30 text-white focus:border-cyan-500 min-h-[100px]"
                    placeholder="Any specific requirements or questions?"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-6"
                >
                  <Send size={18} className="mr-2" />
                  Submit Booking Request
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
